package or.garden.manager.domain

import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.PlantingItem
import or.garden.manager.domain.model.PlantingResponse

internal class PlantingApi {
    suspend fun getPlantings(month: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get("${GardenHttpClient.baseUrl}/plantings/$month")
    }

    suspend fun savePlantings(month: Int, plantings: List<PlantingItem>): Result<HttpResponse> =
        runCatching {
            return@runCatching GARDEN_CLIENT.post("${GardenHttpClient.baseUrl}/plantings/$month") {
                setBody(plantings)
            }
        }

    suspend fun generateAiPlantingPlan(month: Int, lang: String): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get("${GardenHttpClient.baseUrl}/plantings/$month/generate-ai?lang=$lang")
    }
}

private data object PlantingRepository {
    fun getPlantings(month: Int): Flow<ResultState<ApiResponse<PlantingResponse>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = PlantingApi()
                .getPlantings(month)
                .getOrNull()

            if (response != null && response.status.value in 200..299) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load plantings. Please try again!"))
        }
    }

    fun savePlantings(
        month: Int,
        plantings: List<PlantingItem>,
    ): Flow<ResultState<ApiResponse<PlantingResponse>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = PlantingApi()
                .savePlantings(month, plantings)
                .getOrNull()

            if (response != null && response.status.value in 200..299) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load plantings. Please try again!"))
        }
    }

    fun generateAiPlantingPlan(month: Int, lang: String): Flow<ResultState<ApiResponse<List<PlantingItem>>>> = flow {
        emit(ResultState.Loading)
        try {
            val result = PlantingApi().generateAiPlantingPlan(month, lang)
            val response = result.getOrNull()

            if (response != null && response.status.value in 200..299) {
                emit(ResultState.Success(response.body()))
            } else if (response != null) {
                val errorMsg = try {
                    response.body<ApiResponse<String>>().message
                } catch (e: Exception) {
                    "Greška na serveru (${response.status.value})"
                }
                emit(ResultState.Error(errorMsg))
            } else {
                val exception = result.exceptionOrNull()
                emit(ResultState.Error(exception?.message ?: "Mrežna greška - server nije dostupan (404/Connection Refused)"))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Greška u komunikaciji: ${e.message}"))
        }
    }
}

data object PlantingService {
    fun getPlantings(month: Int, result: (ResultState<ApiResponse<PlantingResponse>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            PlantingRepository.getPlantings(month).collect { result(it) }
        }
    }

    fun savePlantings(
        month: Int,
        plantings: List<PlantingItem>,
        result: (ResultState<ApiResponse<PlantingResponse>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            PlantingRepository.savePlantings(month, plantings).collect { result(it) }
        }
    }

    fun generateAiPlantingPlan(
        month: Int,
        lang: String,
        result: (ResultState<ApiResponse<List<PlantingItem>>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            PlantingRepository.generateAiPlantingPlan(month, lang).collect { result(it) }
        }
    }
}
